import { HTMLAttributes, lazy, Suspense, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './CourseCard.module.scss';
import { CourseCardProps } from './CourseCard.types';
import ProgressBar from '../../../common/ProgressBar/ProgressBar';
import { COURSE_CARD_PARAMS } from '../../../../lib/constants/courses';
import { CY_COURSE_CARD_ATTRIBUTES } from '../../../../lib/constants/cypress';
import shortenText from '../../../../lib/utils/shortenText';
import { classes } from '../../../../lib/utils/cssUtils';
import { LearningImage } from '../../../common/Image/LearningImage';
import { SCALE } from '../../../common/Image/ImageScale';
import { isNil } from '../../../../lib/utils/isNil';
import { useDevice } from '../../../../lib/hooks/device';
import SkeletonCourseCard from './SkeletonCourseCard';
import useDuration from '../../../../lib/hooks/useDuration';
import { courseCardIds } from '../../../../lib/tests/testIds';
import CardBadge from './CardBadge';
import { useFiltersView } from '../../../../lib/context/FiltersViewContext';
import { SEARCH_RESULT_CLICK_TRACKING } from '../../../../lib/constants/attributesAnalytics';
import PureLink, { PureLinkVariant } from '../../../common/Link/PureLink';
import LearningLink from '../LearningLink/LearningLink';
import { Role, useRole } from '../../../../lib/context/SessionContext';
import { Label } from '../../../common/Label/Label';
import { COURSE_CARDS_LABEL_KEYS, TRAINING_TYPE_LABEL } from '../../../../lib/constants/labelKeys';
import PureHeading from '../../../common/Heading/PureHeading';
import { HeadingLevel } from '../../../../lib/utils/HeadingMapper';
import TrainingCardScormActions from './TrainingCardScormActions';
import { Api } from '../../../../lib/types/apiSpec';
import { isTrainingType } from '../../../../lib/utils/trainings/typesChecking';
import { DownloadIcon } from '../../../../lib/icons/DowloadIcon';
import { DetailsLinkDestination } from '../../../../lib/hooks/useGetDetailsLink';

const SwiperSlide = lazy(() => import('swiper/react').then(module => ({ default: module.SwiperSlide })));

const PROGRESS_VISIBLE = false;

const CourseCard = ({
  duration,
  dueDate,
  elementsClasses,
  hideImage,
  imageURL,
  position,
  type,
  status,
  progress,
  id,
  originalId,
  name,
  canDownload,
  variant = 'default',
  assetReference,
  thumbnail,
  eventStartDate,
  eventRegistrationUrl,
  eventId,
}: CourseCardProps) => {
  const role = useRole();
  const { withSearch, pageSize } = useFiltersView();
  const location = useLocation();
  const device = useDevice();
  const formattedDuration = useDuration(typeof duration === 'string' ? undefined : duration);
  const WrapperTag = device !== 'desktop' ? 'div' : SwiperSlide;
  const showProgressBar = role !== Role.GUEST && !isNil(progress) && !status && status !== Api.TrainingStatus.COMPLETED;
  const borderStyle = {
    [styles.borderBlue]: type === Api.LearningType.LearningPath,
    [styles.borderOrange]: type === Api.LearningType.Course,
    [styles.borderGrey]: type === Api.LearningType.Document,
    [styles.borderPurple]: type === Api.LearningType.Event,
  };

  const commonLinkProps = {
    checkIfAvailable: false as const,
    type,
    id,
    name,
    ...(withSearch && {
      [SEARCH_RESULT_CLICK_TRACKING.TITLE]: name,
      [SEARCH_RESULT_CLICK_TRACKING.POSITION]: position,
      [SEARCH_RESULT_CLICK_TRACKING.ITEMS]: pageSize,
      [SEARCH_RESULT_CLICK_TRACKING.LOCATION]: `${window.location.origin}${location.pathname}${location.search}${location.hash}`,
    }),
  };

  const WrappedClickableImage = useCallback(
    () => (
      <LearningImage
        id={originalId}
        name={name}
        type={type}
        imageClassName={classes(styles.image, borderStyle)}
        className={styles.imageWrapper}
        imageURL={imageURL ?? thumbnail}
        scale={SCALE.GRID}
        eventStartDate={eventStartDate}
      />
    ),
    [originalId, name, borderStyle, imageURL, thumbnail, type],
  );

  const WrappedClickableTitle = useCallback(
    ({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) => (
      <PureHeading level={HeadingLevel.h3} className={classes(styles.courseTitle, className)} {...props}>
        {shortenText(name, { length: COURSE_CARD_PARAMS.TITLE_LIMIT })}
      </PureHeading>
    ),
    [],
  );

  return (
    <Suspense fallback={<SkeletonCourseCard />}>
      <WrapperTag
        role="group"
        className={classes(styles.container, elementsClasses?.root, {
          [styles.shadowed]: variant === 'shadowed',
        })}
      >
        {!hideImage && (
          <LearningLink
            ContentElement={WrappedClickableImage}
            className={classes(styles.imageContainer, elementsClasses?.image, {
              [styles.eventImageConatiner]: type === Api.LearningType.Event,
            })}
            aria-hidden
            tabIndex={-1}
            data-testid={courseCardIds.imageLink}
            {...commonLinkProps}
          />
        )}
        <div className={classes(styles.content, elementsClasses?.content)}>
          <CardBadge dueDate={dueDate} status={status} className={styles.cardBadge} />
          <Label
            name={TRAINING_TYPE_LABEL(type)}
            className={classes(styles.courseTypeBase, styles.courseType, borderStyle)}
            data-cy={CY_COURSE_CARD_ATTRIBUTES.COURSE_TYPE}
          />
          <LearningLink {...commonLinkProps} className={styles.titleLink} ContentElement={WrappedClickableTitle} />
          {PROGRESS_VISIBLE && showProgressBar && (
            <ProgressBar className={styles.progress} progress={progress} id={id} />
          )}
          {!!formattedDuration && (
            <span
              className={styles.duration}
              data-testid={courseCardIds.duration}
              data-cy={CY_COURSE_CARD_ATTRIBUTES.DURATION}
            >
              {formattedDuration}
            </span>
          )}
          <LearningLink
            {...commonLinkProps}
            status={status ?? Api.TrainingStatus.NOT_STARTED}
            type={type}
            id={id}
            originalId={originalId}
            eventId={eventId}
            eventOriginalId={eventId}
            name={name}
            className={styles.link}
            variant={PureLinkVariant.Link}
            eventRegistrationUrl={role === Role.LEARNER ? eventRegistrationUrl : undefined}
            to={
              role === Role.LEARNER && (isTrainingType(type) || type === Api.LearningType.Event)
                ? DetailsLinkDestination.LITMOS
                : DetailsLinkDestination.INTERNAL
            }
            card
          />
          {isTrainingType(type) && canDownload && <TrainingCardScormActions id={id} type={type} name={name} />}
          {assetReference && (
            <PureLink href={assetReference} className={styles.downloadAnchor} tabBehaviour="forceNewTab">
              <Label name={COURSE_CARDS_LABEL_KEYS.DOWNLOAD_ASSET_LABEL} />
              <DownloadIcon />
            </PureLink>
          )}
        </div>
      </WrapperTag>
    </Suspense>
  );
};

CourseCard.displayName = 'SwiperSlide';

export default CourseCard;
